/**
 * Plans Page - Trade Loop v2.0
 *
 * Displays subscription plan options with feature comparison
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckoutButton } from "@/components/subscription/CheckoutButton";
import { DowngradeConfirmationModal } from "@/components/subscription/DowngradeConfirmationModal";
import { CancelSubscriptionModal } from "@/components/subscription/CancelSubscriptionModal";
import { usePricing } from "@/hooks/usePricing";
import { PLAN_VISIBILITY, isPlanComingSoon } from "@/config/subscription-plan-limits";
import { isNative } from "@/utils/platform";
import { useToast } from "@/contexts/ToastContext";
import {
  User,
  Star,
  Users,
  Check,
  X,
  Info,
  Sparkles,
  ChevronLeft,
} from "lucide-react";

/**
 * In-flight and completed hand-off redemptions, keyed by token.
 *
 * MODULE LEVEL ON PURPOSE — a ref or a state flag lives and dies with the component, and the bug
 * this fixes is precisely a REMOUNT. Observed in production on iOS 2026-08-16: on the cold Safari
 * open the auth state resolves and remounts this page mid-request, so the token was redeemed
 * twice 800ms apart. The first call succeeded, the second got `already_used`, and the second is
 * the one that rendered — the user was told their link had expired immediately after it worked.
 *
 * Every mount now awaits the SAME promise rather than starting its own request, so a token is
 * redeemed at most once no matter how many times this page mounts.
 */
const handoffRedemptions = new Map<string, Promise<string | null>>();

/**
 * Redeem `token` once, ever. Resolves to a Stripe Checkout URL, or null for any failure —
 * expired, already used, unknown, or unreachable, which all land the caller in the same place.
 */
function redeemHandoffOnce(token: string): Promise<string | null> {
  const existing = handoffRedemptions.get(token);
  if (existing) return existing;

  const origin = window.location.origin;
  const attempt = (async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/handoff/redeem`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token,
            successUrl: `${origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${origin}/payment-cancel`
          })
        }
      );

      const result = response.ok ? await response.json() : null;
      if (result?.data?.redeemed && result.data.checkoutUrl) {
        return result.data.checkoutUrl as string;
      }
      return null;
    } catch (error) {
      console.error('[Plans] Checkout hand-off could not be redeemed:', error);
      return null;
    }
  })();

  handoffRedemptions.set(token, attempt);
  return attempt;
}

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PlanCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  price: string;
  priceLabel?: string;
  features: PlanFeature[];
  buttonText: string;
  isPopular?: boolean;
  isCurrentPlan?: boolean;
  showButton?: boolean;
  /**
   * Visible, listed in full, and not for sale (Card 24).
   *
   * Takes precedence over every other button state below, including `showButton` — a signed-out
   * visitor sees no purchase controls at all, and leaving the card with nothing where a button
   * belongs is precisely the "looks purchasable, dead-ends" reading this state exists to remove.
   * Reviewers arrive here signed out via the Card 18 link-out.
   */
  comingSoon?: boolean;
  planType?: 'starter' | 'pro' | 'grow';
  isDowngrade?: boolean;
  onDowngrade?: () => void;
  isCanceled?: boolean;
  onCancel?: () => void;
  onReactivate?: () => void;
  isProcessing?: boolean;
}

function PlanCard({
  icon,
  iconBg,
  title,
  subtitle,
  price,
  priceLabel,
  features,
  buttonText,
  isPopular,
  isCurrentPlan,
  showButton = true,
  comingSoon = false,
  planType,
  isDowngrade,
  onDowngrade,
  isCanceled,
  onCancel,
  onReactivate,
  isProcessing,
}: PlanCardProps) {
  const isHighlighted = isPopular || isCurrentPlan;

  return (
    <Card
      className={`relative transition-all duration-200 hover:shadow-lg ${
        isHighlighted
          ? "border-2 border-blue-500 shadow-md"
          : "border border-gray-200"
      }`}
    >
      <CardContent className="p-6 flex flex-col h-full">
        {/* Badges */}
        {comingSoon && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-gray-700 text-white text-xs font-semibold px-4 py-1 rounded-full shadow-md">
              Coming Soon
            </span>
          </div>
        )}
        {isPopular && !comingSoon && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-semibold px-4 py-1 rounded-full shadow-md">
              Best Value
            </span>
          </div>
        )}
        {isCurrentPlan && (
          <div className="absolute -top-3 right-4">
            <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
              Your Plan
            </span>
          </div>
        )}

        {/* Header - Icon/Title on left, Price on right */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
              {icon}
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">{title}</h3>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">{price}</span>
              {priceLabel && (
                <span className="text-sm text-gray-500">{priceLabel}</span>
              )}
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-3 flex-1 mb-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-2.5">
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                feature.included ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {feature.included ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-red-600" />
                )}
              </div>
              <span className={`text-sm ${feature.included ? 'text-gray-700' : 'text-gray-400'}`}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* Button at bottom */}
        {comingSoon ? (
          /* Card 24. There is no handler here at all — not a handler behind a `disabled` prop.
             `disabled` is a rendering detail a devtools edit removes; an absent onClick and an
             absent CheckoutButton are the actual absence of a checkout path. The sentence below
             carries the state for anyone who cannot see the greyed-out styling, since a disabled
             button is not reachable by keyboard. */
          <div className="mt-auto space-y-2">
            <Button
              disabled
              aria-disabled="true"
              className="w-full h-11 rounded-lg font-semibold bg-gray-100 text-gray-500 cursor-not-allowed"
            >
              Coming Soon
            </Button>
            <p className="text-xs text-gray-500 text-center">
              {title} isn&rsquo;t available to purchase yet.
            </p>
          </div>
        ) : showButton && (
          <>
            {/* Current Plan - Show Cancel/Reactivate */}
            {isCurrentPlan && planType !== 'starter' ? (
              isCanceled ? (
                <Button
                  onClick={onReactivate}
                  disabled={isProcessing}
                  className="w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto bg-green-600 hover:bg-green-700 text-white shadow-md"
                >
                  {isProcessing ? 'Reactivating...' : 'Reactivate Subscription'}
                </Button>
              ) : (
                <Button
                  onClick={onCancel}
                  disabled={isProcessing}
                  variant="outline"
                  className="w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto border-2 border-red-500 text-red-600 hover:bg-red-50 shadow-sm"
                >
                  {isProcessing ? 'Canceling...' : 'Cancel Subscription'}
                </Button>
              )
            ) : isCurrentPlan && planType === 'starter' ? (
              /* Starter plan - Current Plan disabled button */
              <Button
                className="w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto bg-gray-100 text-gray-500 cursor-default"
                disabled={true}
              >
                Current Plan
              </Button>
            ) : isDowngrade ? (
              /* Downgrade Button */
              <Button
                onClick={onDowngrade}
                className="w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-300"
              >
                {buttonText}
              </Button>
            ) : planType && planType !== 'starter' ? (
              /* Upgrade with CheckoutButton for paid plans */
              <CheckoutButton
                plan={planType}
                buttonText={buttonText}
                className={`w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto ${
                  isHighlighted
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              />
            ) : planType === 'starter' ? (
              /* Starter plan - not current */
              <Button
                className="w-full h-11 rounded-lg font-semibold transition-all duration-200 mt-auto bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                {buttonText}
              </Button>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlansPage() {
  const { isAuthenticated, user } = useAuth();
  const { plans, isLoading: isPricingLoading } = usePricing();
  const { showSuccess, showError, showInfo } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Native checkout hand-off redemption (Card 18, task B) ───────────────────────────────────
  //
  // A native user tapped "Upgrade", was sent here in their browser, and is almost certainly NOT
  // signed in on this device. `?h=` is the opaque, single-use, ~15-minute credential that carries
  // their account across that gap. Redeeming it mints a Stripe Checkout session for that account
  // and nothing else — it does not sign anyone in and cannot be made to.
  //
  // A DEAD LINK MUST LAND SOFTLY. Expired, already-used and unknown tokens all fall through to
  // exactly what an anonymous visitor sees: this page, with pricing, plus a sign-in prompt.
  // Never an error page, never a login wall, never an invite wall. App reviewers open these
  // links, often days later and out of order, and anything harsher reads as a broken purchase.
  const handoffToken = searchParams.get('h');
  const [handoffState, setHandoffState] = useState<'idle' | 'redeeming' | 'expired'>(
    handoffToken ? 'redeeming' : 'idle'
  );

  useEffect(() => {
    if (!handoffToken) return;

    // Belt and braces. Redemption ends in a navigation to Stripe, which is lawful in a browser
    // and a policy violation inside the WebView — and this page renders in the app too. A deep
    // link cannot currently carry a query string here (the appUrlOpen handler keeps only the
    // pathname), so this should be unreachable; "should be unreachable" is not a guard.
    if (isNative) {
      setHandoffState('idle');
      return;
    }

    let cancelled = false;

    (async () => {
      const checkoutUrl = await redeemHandoffOnce(handoffToken);

      // ⚠️ DELIBERATELY NOT GUARDED BY `cancelled`. A token is spent the moment the server claims
      // it, so if this component unmounted mid-request there is no second chance to get this URL
      // — discarding it strands the user on "your link expired" one second after their link
      // worked, which is the production bug this replaces. Navigating a page that is unmounting
      // is harmless; throwing away a consumed credential is not.
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      if (cancelled) return;

      // Anything else is the soft landing.
      setHandoffState('expired');

      // Drop the spent token from the URL, so a refresh or a shared screenshot does not carry a
      // credential around. Only on the failure path — the success path leaves for Stripe.
      const next = new URLSearchParams(searchParams);
      next.delete('h');
      setSearchParams(next, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the token alone: this must run once per token, and including
    // searchParams would re-fire it the moment the cleanup above rewrites the URL.
  }, [handoffToken]);

  // Check if user is an organization member - redirect to organization page
  const isOrganizationMember = user?.organizationMembership?.role === 'member';

  // Redirect organization members to organization page
  useEffect(() => {
    if (isOrganizationMember) {
      navigate('/organization', { replace: true });
    }
  }, [isOrganizationMember, navigate]);

  // If user is not authenticated, render without AppLayout (no headers/footers)
  const isPublicView = !isAuthenticated;

  // Normalize subscription plan to lowercase for comparison
  const currentPlan = user?.subscriptionPlan?.toLowerCase() || "starter";

  // Downgrade modal state
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [targetDowngradePlan, setTargetDowngradePlan] = useState<string>('');
  const [isDowngrading, setIsDowngrading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  // Same gap as SubscriptionPage: Cancel fired straight from the plan card with no confirmation.
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch subscription details for effective date and cancel status
  const fetchSubscriptionData = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/current`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data.data?.subscription || null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    }
  };

  // Fetch subscription data on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptionData();
    }
  }, [isAuthenticated]);

  const handleDowngradeClick = (targetPlan: string) => {
    setTargetDowngradePlan(targetPlan);
    setDowngradeModalOpen(true);
  };

  const handleDowngradeConfirm = async () => {
    try {
      setIsDowngrading(true);
      showInfo('Scheduling downgrade...', 'Please wait');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/change-plan`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ newPlan: targetDowngradePlan }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to schedule downgrade';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      const effectiveDate = result.data?.planChange?.effective_date;

      showSuccess(
        'Downgrade Scheduled',
        effectiveDate
          ? `Your subscription will change on ${new Date(effectiveDate).toLocaleDateString()}`
          : 'Your subscription will change at the end of your billing period'
      );

      setDowngradeModalOpen(false);

      // Optionally redirect to subscription page
      setTimeout(() => {
        window.location.href = '/subscription';
      }, 2000);
    } catch (error: any) {
      console.error('Downgrade error:', error);
      showError('Failed to Schedule Downgrade', error.message || 'Unable to schedule downgrade at this time');
    } finally {
      setIsDowngrading(false);
    }
  };

  // Helper to determine if a plan is a downgrade
  const isDowngrade = (targetPlan: string): boolean => {
    const planHierarchy = { starter: 0, pro: 1, grow: 2 };
    return planHierarchy[targetPlan as keyof typeof planHierarchy] < planHierarchy[currentPlan as keyof typeof planHierarchy];
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCanceling(true);
      showInfo('Canceling subscription...', 'Please wait');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to cancel subscription';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      showSuccess('Subscription Canceled', 'Your subscription will remain active until the end of the billing period');

      // Refresh subscription data
      await fetchSubscriptionData();
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      showError('Failed to Cancel', error.message || 'Unable to cancel subscription at this time');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsCanceling(true);
      showInfo('Reactivating subscription...', 'Please wait');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/workflows/subscriptions/reactivate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to reactivate subscription';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      showSuccess('Subscription Reactivated', 'Your subscription has been successfully reactivated');

      // Refresh subscription data
      await fetchSubscriptionData();
    } catch (error: any) {
      console.error('Reactivate subscription error:', error);
      showError('Failed to Reactivate', error.message || 'Unable to reactivate subscription at this time');
    } finally {
      setIsCanceling(false);
    }
  };

  const starterFeatures: PlanFeature[] = [
    { text: "Unlimited referrals", included: true },
    { text: "Unlimited partner connections", included: true },
    { text: "1 trade selection", included: true },
    { text: "Basic profile sharing", included: true },
    { text: "Standard messaging", included: true },
    { text: "Community support", included: true },
    { text: "Company logo & branding", included: false },
    { text: "Partner favorites", included: false },
  ];

  const proFeatures: PlanFeature[] = [
    { text: "Unlimited referrals", included: true },
    { text: "Unlimited partner connections", included: true },
    { text: "Unlimited trades", included: true },
    { text: "Company logo & branding", included: true },
    { text: "Partner favorites & filtering", included: true },
    { text: "Priority support", included: true },
    { text: "Team collaboration tools", included: false },
    { text: "Custom integrations", included: false },
  ];

  const growFeatures: PlanFeature[] = [
    { text: "Unlimited referrals", included: true },
    { text: "Unlimited partner connections", included: true },
    { text: "Unlimited trades", included: true },
    { text: "Company logo & branding", included: true },
    { text: "Partner favorites", included: true },
    { text: "Team collaboration tools", included: true },
    { text: "Custom integrations", included: true },
    { text: "Priority support", included: true },
  ];

  // Comparison features for the table
  const comparisonFeatures = [
    { name: "Send Referrals", starter: "Unlimited", pro: "Unlimited", grow: "Unlimited" },
    { name: "Receive Referrals", starter: "Unlimited", pro: "Unlimited", grow: "Unlimited" },
    { name: "Partner Connections", starter: "Unlimited", pro: "Unlimited", grow: "Unlimited" },
    { name: "Additional Trades", starter: "Primary only", pro: "Unlimited", grow: "Unlimited" },
    { name: "Company Logo & Branding", starter: false, pro: true, grow: true },
    { name: "Partner Favorites", starter: false, pro: true, grow: true },
    { name: "Team Collaboration", starter: false, pro: false, grow: true },
    { name: "Custom Integrations", starter: false, pro: false, grow: true },
    { name: "Priority Support", starter: false, pro: true, grow: true },
  ];

  const pageContent = (
    <div className="max-w-6xl mx-auto p-4 pb-[41px] md:pb-4 space-y-8">
      {/* Back to Landing button for public users */}
      {isPublicView && (
        <div className="mb-6">
          <Link to="/">
            <Button variant="outline" className="flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      )}

      {/* Hand-off in flight. Pricing stays rendered underneath, so a slow or failing redemption
          degrades into the ordinary page rather than a blank screen. */}
      {handoffState === 'redeeming' && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
          <p className="text-sm text-blue-900">Taking you to secure checkout...</p>
        </div>
      )}

      {/* The soft landing. Deliberately not an error: the link simply aged out, and the next
          step is signing in — which is also the ONLY thing this page ever asks of someone whose
          link has expired. No invite wall, no dead end. */}
      {handoffState === 'expired' && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-1">That checkout link has expired</h3>
          <p className="text-sm text-gray-600 mb-4">
            Checkout links are valid for 15 minutes and can only be used once. Sign in and pick
            your plan below to pick up where you left off.
          </p>
          {!isAuthenticated && (
            <Link to="/login?redirect=/plans">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">Sign in to continue</Button>
            </Link>
          )}
        </div>
      )}

      {/* Signed-out visitors get no purchase buttons at all (showButton={isAuthenticated}), which
          left this page a dead end: prices, a comparison table, and nothing to act on. Worse for
          an App Store reviewer, who follows the link-out, lands here without a session and has no
          way to tell whether the page is broken or they simply need to sign in.

          Suppressed when the expired-hand-off notice is up, since that already prompts to sign in
          and two sign-in prompts stacked is worse than one.

          Wording is lifted from the sign-up form so the page and that form agree. It no longer
          mentions an access key: signup opened to everyone on 2026-08-31. Keep release-stage
          words ("beta", "early access") out of this copy — store-facing wording is a documented
          rejection risk (Card 11). */}
      {isPublicView && handoffState !== 'expired' && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <h3 className="font-semibold text-gray-900 mb-1">You're not signed in</h3>
          <p className="text-sm text-gray-700 mb-4">
            Sign in to choose or change your plan, or create an account — it only takes a minute.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/login?redirect=/plans" className="sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white">
                Sign in
              </Button>
            </Link>
            {/* The combined sign-in/sign-up page opened on the sign-up tab. It offers Continue
                with Google / Apple and lets the user toggle back to signing in without losing
                their place. (The standalone /signup form this once contrasted with has since
                been retired; /signup now redirects here.) */}
            <Link to="/login?mode=signup&redirect=/plans" className="sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto">
                Create an account
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Title Section */}
      <div className="text-center pt-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          Choose Your Plan
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Scale your referral network with the right plan for your business
        </p>
      </div>

      {/* Pricing Loading State */}
      {isPricingLoading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-sm text-gray-500">Loading pricing...</p>
        </div>
      )}

        {/* Upgrade Banner - Only show for Starter plan users */}
        {isAuthenticated && currentPlan === "starter" && (
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white shadow-md">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Ready to unlock more features?</h3>
                <p className="text-blue-50 mb-4">
                  Upgrade your plan to get access to unlimited trade selections, company branding, and priority support. Scale your referral network to the next level.
                </p>
                <div className="flex items-center gap-2 text-sm text-blue-100">
                  <Info className="w-4 h-4" />
                  <span>You're currently on the Starter plan - Limited to 1 trade selection</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plan Cards */}
        <div className={`grid grid-cols-1 ${PLAN_VISIBILITY.grow ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
          {/* Starter Plan */}
          <PlanCard
            icon={<User className="w-6 h-6 text-gray-600" />}
            iconBg="bg-gray-100"
            title="Starter"
            subtitle="Perfect for getting started"
            price="Free"
            features={starterFeatures}
            buttonText={isAuthenticated && isDowngrade('starter') ? "Change to Starter" : "Get Started"}
            isCurrentPlan={isAuthenticated && currentPlan === "starter"}
            showButton={isAuthenticated}
            planType="starter"
            isDowngrade={isAuthenticated && isDowngrade('starter')}
            onDowngrade={() => handleDowngradeClick('starter')}
            isCanceled={subscriptionData?.cancelAtPeriodEnd}
            onCancel={() => setShowCancelModal(true)}
            onReactivate={handleReactivateSubscription}
            isProcessing={isCanceling}
          />

          {/* Pro Plan */}
          <PlanCard
            icon={<Star className="w-6 h-6 text-white" />}
            iconBg="bg-gradient-to-br from-blue-500 to-indigo-500"
            title="Pro"
            subtitle="For active professionals"
            price={plans?.pro?.formatted.split('/')[0] || "$29"}
            priceLabel="/month"
            features={proFeatures}
            buttonText={isAuthenticated && isDowngrade('pro') ? "Change to Pro" : "Upgrade to Pro"}
            isPopular={true}
            isCurrentPlan={isAuthenticated && currentPlan === "pro"}
            showButton={isAuthenticated}
            planType="pro"
            isDowngrade={isAuthenticated && isDowngrade('pro')}
            onDowngrade={() => handleDowngradeClick('pro')}
            isCanceled={subscriptionData?.cancelAtPeriodEnd}
            onCancel={() => setShowCancelModal(true)}
            onReactivate={handleReactivateSubscription}
            isProcessing={isCanceling}
          />

          {/* Grow Plan — visible so users can see what is planned, NOT purchasable (Card 24).
              `comingSoon` is derived from the plan config rather than hardcoded here, so the day
              Grow ships this card starts selling it without being edited. An existing Grow
              subscriber (comped, or from before Grow was withdrawn) still gets their normal
              Cancel/Reactivate controls — being unable to buy a plan is not a reason to strand
              someone who is already on it. */}
          {PLAN_VISIBILITY.grow && (
            <PlanCard
              icon={<Users className="w-6 h-6 text-white" />}
              iconBg="bg-gradient-to-br from-indigo-500 to-purple-500"
              title="Grow"
              subtitle="For your professional team"
              price={plans?.grow?.formatted.split('/')[0] || "$79"}
              priceLabel="/month"
              features={growFeatures}
              buttonText="Upgrade to Grow"
              isCurrentPlan={isAuthenticated && currentPlan === "grow"}
              showButton={isAuthenticated}
              comingSoon={isPlanComingSoon('grow') && currentPlan !== 'grow'}
              planType="grow"
              isDowngrade={false}
              isCanceled={subscriptionData?.cancelAtPeriodEnd}
              onCancel={() => setShowCancelModal(true)}
              onReactivate={handleReactivateSubscription}
              isProcessing={isCanceling}
            />
          )}
        </div>

        {/* Feature Comparison Table */}
        <div className="pt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Compare Plans
          </h3>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                        Features
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                        Starter
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                        Pro
                      </th>
                      {PLAN_VISIBILITY.grow && (
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                          Grow
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {comparisonFeatures.map((feature, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                          {feature.name}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {typeof feature.starter === 'boolean' ? (
                            feature.starter ? (
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-red-600 mx-auto" />
                            )
                          ) : (
                            <span className="text-sm text-gray-700">{feature.starter}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {typeof feature.pro === 'boolean' ? (
                            feature.pro ? (
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-red-600 mx-auto" />
                            )
                          ) : (
                            <span className="text-sm text-gray-700">{feature.pro}</span>
                          )}
                        </td>
                        {PLAN_VISIBILITY.grow && (
                          <td className="px-6 py-4 text-center">
                            {typeof feature.grow === 'boolean' ? (
                              feature.grow ? (
                                <Check className="w-5 h-5 text-green-600 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-red-600 mx-auto" />
                              )
                            ) : (
                              <span className="text-sm text-gray-700">{feature.grow}</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
    </div>
  );

  // For authenticated users, wrap in AppLayout (includes headers/footers)
  // For public users, render content directly (no headers/footers)
  return (
    <>
      {isPublicView ? (
        <div className="min-h-screen bg-gray-50 pt-5 md:pt-0">
          {pageContent}
        </div>
      ) : (
        <AppLayout activeTab="profile" title="Plans & Usage" backLink="/settings">
          {pageContent}
        </AppLayout>
      )}

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        currentPlan={currentPlan}
        periodEndDate={
          subscriptionData?.currentPeriodEnd ? new Date(subscriptionData.currentPeriodEnd) : undefined
        }
        isLoading={isCanceling}
      />

      {/* Downgrade Confirmation Modal */}
      <DowngradeConfirmationModal
        isOpen={downgradeModalOpen}
        onClose={() => setDowngradeModalOpen(false)}
        onConfirm={handleDowngradeConfirm}
        currentPlan={currentPlan}
        targetPlan={targetDowngradePlan}
        effectiveDate={subscriptionData?.currentPeriodEnd ? new Date(subscriptionData.currentPeriodEnd) : undefined}
        isLoading={isDowngrading}
      />
    </>
  );
}
